import React, { useState, useEffect } from 'react';
import { Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const limit = 20;

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = { limit, skip: page * limit };
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await axios.get(`${API}/admin/orders`, { params, withCredentials: true });
      setOrders(response.data.orders);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, [statusFilter, page]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${newStatus}`, {}, { withCredentials: true });
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const formatPrice = (price) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 }).format(price);
  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-medium text-[#F8F8F8]">Orders</h1>
          <p className="text-[#A0A0A0] mt-1">{total} total orders</p>
        </div>
        <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value); setPage(0); }}>
          <SelectTrigger className="w-[180px] bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] rounded-none">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-[#333333]">
            <SelectItem value="all" className="text-[#F8F8F8]">All Orders</SelectItem>
            <SelectItem value="pending" className="text-[#F8F8F8]">Pending</SelectItem>
            <SelectItem value="paid" className="text-[#F8F8F8]">Paid</SelectItem>
            <SelectItem value="shipped" className="text-[#F8F8F8]">Shipped</SelectItem>
            <SelectItem value="delivered" className="text-[#F8F8F8]">Delivered</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-[#111111] border border-[#222222]">
        {loading ? (
          <div className="p-8 text-center text-[#A0A0A0]">Loading...</div>
        ) : orders.length === 0 ? (
          <div className="p-8 text-center text-[#A0A0A0]">No orders found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#222222] bg-[#0A0A0A]">
                  <th className="text-left text-[#A0A0A0] text-sm font-medium py-3 px-4">Order ID</th>
                  <th className="text-left text-[#A0A0A0] text-sm font-medium py-3 px-4">Customer</th>
                  <th className="text-left text-[#A0A0A0] text-sm font-medium py-3 px-4">Items</th>
                  <th className="text-left text-[#A0A0A0] text-sm font-medium py-3 px-4">Total</th>
                  <th className="text-left text-[#A0A0A0] text-sm font-medium py-3 px-4">Status</th>
                  <th className="text-left text-[#A0A0A0] text-sm font-medium py-3 px-4">Date</th>
                  <th className="text-left text-[#A0A0A0] text-sm font-medium py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id} className="border-b border-[#222222] hover:bg-[#1A1A1A]">
                    <td className="py-3 px-4"><span className="text-[#00F0FF] font-mono text-sm">{order.id.slice(0, 8)}...</span></td>
                    <td className="py-3 px-4">
                      <p className="text-[#F8F8F8] text-sm">{order.customer?.first_name} {order.customer?.last_name}</p>
                      <p className="text-[#666666] text-xs">{order.customer?.email}</p>
                    </td>
                    <td className="py-3 px-4 text-[#F8F8F8] text-sm">{order.items?.length || 1}</td>
                    <td className="py-3 px-4 text-[#F8F8F8] font-medium">{formatPrice(order.total)}</td>
                    <td className="py-3 px-4">
                      <Select value={order.status} onValueChange={(value) => updateStatus(order.id, value)}>
                        <SelectTrigger className={`w-[120px] h-8 text-xs rounded-none border-0 ${
                          order.status === 'paid' ? 'bg-[#34C759]/20 text-[#34C759]' :
                          order.status === 'pending' ? 'bg-[#FFCC00]/20 text-[#FFCC00]' :
                          order.status === 'shipped' ? 'bg-[#00F0FF]/20 text-[#00F0FF]' :
                          order.status === 'delivered' ? 'bg-[#34C759]/20 text-[#34C759]' :
                          'bg-[#666666]/20 text-[#666666]'
                        }`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-[#333333]">
                          <SelectItem value="pending" className="text-[#F8F8F8]">Pending</SelectItem>
                          <SelectItem value="paid" className="text-[#F8F8F8]">Paid</SelectItem>
                          <SelectItem value="shipped" className="text-[#F8F8F8]">Shipped</SelectItem>
                          <SelectItem value="delivered" className="text-[#F8F8F8]">Delivered</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-3 px-4 text-[#A0A0A0] text-sm">{formatDate(order.created_at)}</td>
                    <td className="py-3 px-4">
                      <Button variant="ghost" size="sm" onClick={() => setSelectedOrder(order)} className="text-[#A0A0A0] hover:text-[#F8F8F8]">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-[#222222]">
            <p className="text-[#A0A0A0] text-sm">Page {page + 1} of {totalPages}</p>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                className="border-[#333333] text-[#A0A0A0] hover:bg-[#1A1A1A] rounded-none">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}
                className="border-[#333333] text-[#A0A0A0] hover:bg-[#1A1A1A] rounded-none">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="bg-[#111111] border-[#222222] text-[#F8F8F8] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">Order Details</DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6 mt-4">
              <div className="bg-[#1A1A1A] p-4 border border-[#222222]">
                <p className="text-[#A0A0A0] text-sm">Order ID</p>
                <p className="text-[#00F0FF] font-mono">{selectedOrder.id}</p>
              </div>

              <div>
                <h3 className="text-[#F8F8F8] font-medium mb-3">Customer</h3>
                <div className="bg-[#1A1A1A] p-4 border border-[#222222] space-y-2">
                  <p className="text-[#F8F8F8]">{selectedOrder.customer?.first_name} {selectedOrder.customer?.last_name}</p>
                  <p className="text-[#A0A0A0] text-sm">{selectedOrder.customer?.email}</p>
                  <p className="text-[#A0A0A0] text-sm">{selectedOrder.customer?.phone}</p>
                  <p className="text-[#A0A0A0] text-sm">
                    {selectedOrder.customer?.address_line1}, {selectedOrder.customer?.suburb} {selectedOrder.customer?.state} {selectedOrder.customer?.postcode}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-[#F8F8F8] font-medium mb-3">Items</h3>
                <div className="space-y-3">
                  {selectedOrder.items?.map((item, index) => (
                    <div key={index} className="bg-[#1A1A1A] p-4 border border-[#222222]">
                      <p className="text-[#F8F8F8]">{item.car?.year} {item.car?.brand} {item.car?.model}</p>
                      <p className="text-[#A0A0A0] text-sm">{item.product?.product_type} {item.product?.colour ? `- ${item.product.colour}` : ''}</p>
                      {item.product?.include_installation_kit && <p className="text-[#A0A0A0] text-sm">+ Installation Kit</p>}
                      <p className="text-[#00F0FF] font-medium mt-2">{formatPrice((item.prices?.product_price || 0) + (item.prices?.installation_kit_price || 0))}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#1A1A1A] p-4 border border-[#222222]">
                <div className="flex justify-between mb-2">
                  <span className="text-[#A0A0A0]">Subtotal</span>
                  <span className="text-[#F8F8F8]">{formatPrice(selectedOrder.subtotal)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-[#A0A0A0]">Shipping</span>
                  <span className="text-[#F8F8F8]">{formatPrice(selectedOrder.shipping)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-[#333333]">
                  <span className="text-[#F8F8F8] font-medium">Total</span>
                  <span className="text-[#00F0FF] font-bold text-lg">{formatPrice(selectedOrder.total)}</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOrdersPage;
